import { fabric } from 'fabric';
import { toMap } from '@/views/tower/v2/objects/utils/fabricUtils';
import { customEvents } from '@/store/CustomEvents';
import ArrowHead, { TYPE as ArrowHeadType } from './ArrowHead';
import LineBody, { TYPE as LineBodyType } from './LineBody';
import TickMark, { TYPE as TickMarkType, TickMarkOptionsBuilder } from './TickMark';
import { compactMoney } from '@/utilities/Money';
import { TickMarkOptions } from './TickMark';
import { ProgramPolicy } from '@/types/Programs';

export const TYPE = 'tower-scale';

export type TowerScaleMode = 'dynamic' | 'aggregate' | 'occurrence';

const parts = {
  head: ArrowHeadType,
  body: LineBodyType,
  tick: TickMarkType,
} as const;

/**
 * TowerScale is a custom fabric object that aids in the creation of the basic policy tower.
 * It consists of a line with tick marks that indicate attachment points representing the
 * start/end of a particular policy's premium coverage amount.
 * @extends fabric.Group
 *
 * @property {string}   type - The fabric object's type.
 */
const TowerScale = fabric.util.createClass(fabric.Group, {
  type: TYPE,
  snappable: true,
  policies: new Map<string, fabric.PolicyRect>(),
  policyListUpdated: false,
  initialize(opts: fabric.ScaleOptions) {
    // instance bound handlers and functions
    const _handleObjectModified = handleObjectModified.bind(this);
    const _handlePolicySync = handlePolicySync.bind(this);
    const _createArrowHead = createArrowHead.bind(this);
    const _createLineBody = createLineBody.bind(this);

    // cleanup when the object is removed
    this.on('removed', () => {
      removeEventListener(customEvents.objectModified, _handleObjectModified as EventListener);
      removeEventListener(customEvents.syncTowerScalePolicies, _handlePolicySync as EventListener);
    });

    // disable the default controls except for the mt (middle top) and mb (middle bottom)
    for (const controlKey of ['mtr', 'tl', 'tr', 'mr', 'br', 'bl', 'ml']) {
      this.setControlVisible(controlKey, false);
    }

    const requiredOptions = {
      cornerStyle: 'circle',
      cornerColor: 'white',
      cornerSize: 10,
      transparentCorners: false,
      objectCaching: false,
    } as fabric.ScaleOptions;

    this.scaleMode = 'dynamic';

    const fabricObjs = opts.objects || [];
    this.partsMap = toMap('id', fabricObjs);

    // Create the arrow head
    const arrowHead = _createArrowHead(this.partsMap.get(parts.head) as fabric.ITriangleOptions);
    this.partsMap.set(parts.head, arrowHead);

    // Create the line body
    const lineBody = _createLineBody(this.partsMap.get(parts.body) as fabric.ILineOptions, arrowHead);
    this.partsMap.set(parts.body, lineBody);

    // Create the tick marks (if they exist)
    const tickMarks = Array.from(this.partsMap.values() as fabric.Object[])
      .filter((part: fabric.Object) => part.id.includes(parts.tick))
      .map((t) => new TickMark(new TickMarkOptionsBuilder(t as TickMarkOptions).build()));
    if (tickMarks.length > 0) {
      for (const tickMark of tickMarks) {
        this.partsMap.set(tickMark.id, tickMark);
      }
    }

    this.callSuper('initialize', Array.from(this.partsMap.values()), { ...opts, ...requiredOptions });

    if (opts.policies) {
      this.policies = new Map(JSON.parse(opts.policies));
    }

    addEventListener(customEvents.objectModified, _handleObjectModified as EventListener);
    addEventListener(customEvents.syncTowerScalePolicies, _handlePolicySync as EventListener);

    this.on('scaling', () => {
      // clear all tick marks
      this.remove(...this.getObjects().filter((obj: fabric.Object) => obj.type?.includes(TickMarkType)));
    });
  },

  toObject() {
    return fabric.util.object.extend(this.callSuper('toObject'), {
      id: this.id,
      policies: JSON.stringify(Array.from(this.policies?.entries())),
      scaleMode: this.scaleMode,
    });
  },

  setMode(mode: TowerScaleMode): void {
    this.scaleMode = mode;
    handleObjectModified.bind(this)();
    this.canvas.setActiveObject(this);
    this.canvas.requestRenderAll();
  }
});

TowerScale.fromObject = function (obj: fabric.Object, fn: (obj: fabric.Object) => void) {
  const towerScale = new TowerScale(obj);
  if (fn) fn(towerScale);
  return towerScale;
};

export default TowerScale;

// ------------------
// private functions
// ------------------
function createArrowHead(this: fabric.TowerScale, options: fabric.ITriangleOptions): fabric.ArrowHead {
  const arrowHead: fabric.ArrowHead = new ArrowHead(options);

  arrowHead.on('scaling', () => {
    const newHeight = (arrowHead.height ?? 0) * (arrowHead.scaleY ?? 0),
      newWidth = (arrowHead.width ?? 0) * (arrowHead.scaleX ?? 0);
    arrowHead.set({
      height: newHeight,
      width: newWidth,
      scaleX: this.scaleX ? 1 / this.scaleX : 1,
      scaleY: this.scaleY ? 1 / this.scaleY : 1,
    });

    this.addWithUpdate();
  });
  return arrowHead;
}

function createLineBody(this: fabric.TowerScale, options: fabric.ILineOptions, arrowHead: fabric.ArrowHead): fabric.LineBody {
  const lineBody = new LineBody(options);

  // Scaling handler for the line body.
  // The scaling is negated and only allows resizing of the line.
  // The tick marks are recreated based on the new line length.
  lineBody.on('scaling', () => {
    const newHeight = (lineBody.height ?? 0) * (lineBody.scaleY ?? 0),
      newWidth = (lineBody.width ?? 0) * (lineBody.scaleX ?? 0),
      newTop = (arrowHead.top ?? 0) + (arrowHead.height ?? 0);

    lineBody.set({
      height: newHeight,
      width: newWidth,
      scaleY: this.scaleY ? 1 / this.scaleY : 1,
      scaleX: this.scaleX ? 1 / this.scaleX : 1,
      top: newTop,
    });
  });

  return lineBody;
}

function createTickMarks(this: fabric.TowerScale): fabric.TickMark[] {
  // when creating tick marks, we need to make sure that the grouped policies 
  // are ungrouped to calculate the tick marks
  if (this.canvas?.getActiveObject()?.type === 'activeSelection') {
    this.canvas.discardActiveObject();
  }

  // clear all tick marks
  this.remove(...this.getObjects().filter((obj) => obj.type?.includes(TickMarkType)));

  if (this.policies.size === 0) return [];

  let minY = Infinity;
  let maxY = -Infinity;

  // Calculate the minimum and maximum Y values among all policies
  for (const policy of this.policies.values()) {
    policy.setCoords();
    const policyCorners = policy.aCoords!;
    const policyTopY = Math.min(
      policyCorners.tl.y,
      policyCorners.tr.y,
      policyCorners.bl.y,
      policyCorners.br.y
    );
    const policyBottomY = Math.max(
      policyCorners.tl.y,
      policyCorners.tr.y,
      policyCorners.bl.y,
      policyCorners.br.y
    );

    if (policyTopY < minY) {
      minY = policyTopY;
    }
    if (policyBottomY > maxY) {
      maxY = policyBottomY;
    }
  }

  const totalHeightOfPolicies = maxY - minY;

  // Resize the line body to fit the policies
  const lineBody = this.partsMap.get(parts.body) as fabric.LineBody;
  lineBody.set({ height: totalHeightOfPolicies + 20 });
  this.addWithUpdate();

  // Adjust the tower scale's top position to align with the topmost policy with padding
  this.set({
    top: minY - 30,
  });
  this.addWithUpdate();

  // Sort policies based on their top positions (ascending order)
  const sortedPolicies = Array.from(this.policies.values()).sort((a, b) => {
    a.setCoords();
    b.setCoords();
    const aPolicyTopY = Math.min(
      a.aCoords!.tl.y,
      a.aCoords!.tr.y,
      a.aCoords!.bl.y,
      a.aCoords!.br.y
    );
    const bPolicyTopY = Math.min(
      b.aCoords!.tl.y,
      b.aCoords!.tr.y,
      b.aCoords!.bl.y,
      b.aCoords!.br.y
    );
    return aPolicyTopY - bPolicyTopY;
  });

  // Calculate limit values based on scale mode
  const reversedOrderPoliciesLimitValues = sortedPolicies.map((policy) => {
    const policyData = policy.getPolicyData();

    switch (this.scaleMode) {
      case 'occurrence':
        return policyData?.occurrenceLimit ?? 0;
      case 'aggregate':
        return policyData?.aggregateLimit ?? 0;
      case 'dynamic':
      default:
        return Math.max(
          policyData?.aggregateLimit ?? 0,
          policyData?.occurrenceLimit ?? 0,
          policyData?.quotaShareTotal ? Number(policyData.quotaShareTotal) : 0
        );
    }
  });

  // Map policies to calculate tick mark positions and labels
  const policies = sortedPolicies.map((policy, index) => {
    policy.setCoords()
    const policyCorners = policy.aCoords!;
    const policyTopY = Math.min(
      policyCorners.tl.y,
      policyCorners.tr.y,
      policyCorners.bl.y,
      policyCorners.br.y
    );

    // Transform policyTopY into the tower scale's coordinate system
    const transformedPoint = fabric.util.transformPoint(
      new fabric.Point(policyCorners.tl.x, policyTopY),
      fabric.util.invertTransform(this.calcTransformMatrix())
    );

    return {
      top: transformedPoint.y,
      label: reversedOrderPoliciesLimitValues
        .slice(index)
        .reduce((acc, curr) => acc + curr, 0),
    };
  });

  // Create tick marks at calculated positions with appropriate labels
  const tickMarks: fabric.TickMark[] = [];
  for (const policy of policies) {
    const tickMarkOptions = new TickMarkOptionsBuilder()
      .withTickMarkOptions({ top: policy.top })
      .withTextOptions({ text: compactMoney(policy.label) })
      .build();

    tickMarks.push(new TickMark(tickMarkOptions));
  }

  return tickMarks;
}

function addTickMarks(this: fabric.TowerScale, tickMarks: fabric.TickMark[]): void {
  for (const tick of tickMarks) {
    this.add(tick);
  }
  this.setCoords();
  this.addWithUpdate();
}

function handleObjectModified(this: fabric.TowerScale): void {
  // if the tower scale is part of an active selection, we don't want to rerender tick marks
  const containsTowerScale = this.canvas?.getActiveObjects().some((obj) => obj.type === TYPE);
  if (this.canvas?.getActiveObject()?.type === 'activeSelection' && containsTowerScale) {
    return;
  }

  syncSnappedPolicyRects.call(this);
  if (this.policyListUpdated) {
    addTickMarks.call(this, createTickMarks.call(this));
    this.policyListUpdated = false;
  }
}

function handlePolicySync(this: fabric.TowerScale, e: CustomEvent<{ policy: ProgramPolicy }>): void {
  // if the tower scale is part of an active selection, we don't want to rerender tick marks
  const containsTowerScale = this.canvas?.getActiveObjects().some((obj) => obj.type === TYPE);
  if (this.canvas?.getActiveObject()?.type === 'activeSelection' && containsTowerScale) {
    return;
  }

  const storedPolicyRectIds = Array.from(this.policies.values()).map((policyRect) => (policyRect.policy as ProgramPolicy)?.id);
  if (storedPolicyRectIds.includes(e.detail.policy.id)) {
    syncSnappedPolicyRects.call(this);
    if (this.policyListUpdated) {
      addTickMarks.call(this, createTickMarks.call(this));
      this.policyListUpdated = false;
    }
  }
}

function syncSnappedPolicyRects(this: fabric.TowerScale): void {
  this.policies = this.policies ?? new Map<string, fabric.PolicyRect>();

  // go through each object in the canvas and determine whether it's touching the tower scale
  const canvasObjects = this.canvas?.getObjects() ?? [];
  const policyRects = canvasObjects.filter((obj) => obj instanceof fabric.PolicyRect)

  // remove the policy rect from the map if it's no longer on the canvas
  const currentPolicyRectIdsOnCanvas = policyRects.map((obj) => obj.id);
  const storedPolicyRectIds = Array.from(this.policies.keys());
  for (const id of storedPolicyRectIds) {
    if (!currentPolicyRectIdsOnCanvas.includes(id)) {
      this.policies.delete(id);
      this.policyListUpdated = true;
    }
  }

  for (const object of policyRects) {
    // remove the policy rect from the map if it's no longer touching the tower scale
    if (!isRectAttached.call(this, object) && object instanceof fabric.PolicyRect && this.policies.has(object.id)) {
      this.policies.delete(object.id);
      this.policyListUpdated = true;
    }

    // add the policy rect to the map if it's touching the tower scale
    if (isRectAttached.call(this, object) && object instanceof fabric.PolicyRect) {
      this.policies.set(object.id, object as fabric.PolicyRect);
      this.policyListUpdated = true;
    }
  }
}

function isRectAttached(this: fabric.TowerScale, rect: fabric.Object): boolean {
  let rectLeft = rect?.left ?? 0;
  let rectTop = rect?.top ?? 0;
  const activeObj = this.canvas?.getActiveObject();
  const activeObjs = this.canvas?.getActiveObjects();

  // if the object is an active selection, we need to transform the rect's top and left to be relative to canvas
  // the transform should only apply if the rect is part of the active selection
  if (activeObj instanceof fabric.ActiveSelection && activeObjs?.includes(rect)) {
    rectLeft = fabric.util.transformPoint(
      new fabric.Point(rect?.left ?? 0, 0),
      activeObj.calcTransformMatrix()
    ).x;

    rectTop = fabric.util.transformPoint(
      new fabric.Point(0, rect?.top ?? 0),
      activeObj.calcTransformMatrix()
    ).y;
  }

  const scaleTop = (this.top ?? 0) - 5;
  const scaleBottom = (this.top ?? 0) + this.getScaledHeight() + 5;
  const rectBottom = rectTop + rect.getScaledHeight();

  const sideTouching = Math.abs((rectLeft ?? 0) - ((this.left ?? 0) + (this.getScaledWidth() ?? 0))) < 5;
  const topTouching = (rectTop < scaleBottom && rectTop > scaleTop);
  const bottomTouching = (rectBottom > scaleTop && rectBottom < scaleBottom);

  return sideTouching && (topTouching || bottomTouching);
}
